import { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";

/**
 * Hook to get elapsed time since createdAt in { hours, minutes, seconds } format
 * @param createdAt - ISO string e.g. "2025-10-02T06:06:24Z"
 */
export function useElapsedTime(createdAt: string) {
  const { t } = useTranslation();
  const [elapsed, setElapsed] = useState(getElapsed(createdAt, t));

  useEffect(() => {
    // Immediately recompute when createdAt changes
    setElapsed(getElapsed(createdAt, t));

    const interval = setInterval(() => {
      setElapsed(getElapsed(createdAt, t));
    }, 1000);

    return () => clearInterval(interval);
  }, [createdAt, t]);

  return elapsed;
}

/**
 * Pure helper function - returns elapsed time
 */
function getElapsed(createdAt: string, t: (key: string, opts?: any) => any) {
  if (!createdAt || isNaN(new Date(createdAt).getTime())) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      diff: 0,
      calculateCost: () => "0.00",
    };
  }

  const start = new Date(createdAt).getTime();
  const now = Date.now();
  const diff = now - start;

  const seconds = Math.floor(diff / 1000) % 60;
  const minutes = Math.floor(diff / (1000 * 60)) % 60;
  const hours = Math.floor(diff / (1000 * 60 * 60)) % 24;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  const calculateCost = (elapsedMs: number, ratePerHour = 0.4): string => {
    const hours = elapsedMs / (1000 * 60 * 60);
    return Number(hours * ratePerHour).toFixed(2);
  };

  return { days, hours, minutes, seconds, diff, calculateCost };
}
